import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from '../utils/logger';

const SYSTEM_COLUMNS = ['record_id', 'file_id', 'claims', 'outreach_status', 'claimed'];

const COMPACT_COLUMNS = [
  'Instellingsnaam',
  'Vestigingsadres Plaats',
  'URL',
  'Telefoonnummer',
  'Filter1',
  'Filter2',
  'Filter3',
  'Notities',
];

const CONTACTS_COLUMNS = [
  ...COMPACT_COLUMNS,
  'Telefoonnummer Mobiel',
  'LinkedIn Link',
  'Company.Info Link',
  'CP Titel Verantwoordelijk Bedrijfsvoering',
  'CP Voorletters Verantwoordelijk Bedrijfsvoering',
  'CP Tussenvoegsel Verantwoordelijk Bedrijfsvoering',
  'CP Achternaam Verantwoordelijk Bedrijfsvoering',
];

function getStorageKey(userId) {
  return `salinator.outreachTableSettings.${userId}`;
}

function buildPreset(presetId, allColumns) {
  const userColumns = allColumns.filter(c => !SYSTEM_COLUMNS.includes(c));
  let visible;
  switch (presetId) {
    case 'compact':
      visible = COMPACT_COLUMNS.filter(c => userColumns.includes(c));
      break;
    case 'contacts':
      visible = CONTACTS_COLUMNS.filter(c => userColumns.includes(c));
      break;
    case 'full':
      visible = [...userColumns];
      break;
    default:
      visible = COMPACT_COLUMNS.filter(c => userColumns.includes(c));
  }
  return {
    preset: presetId,
    visibleColumns: visible,
    columnOrder: userColumns,
    columnWidths: {},
    pinFirstColumn: true,
  };
}

function reconcileWithBackendColumns(settings, backendColumns) {
  if (!settings || !backendColumns || backendColumns.length === 0) return settings;

  const userColumns = backendColumns.filter(c => !SYSTEM_COLUMNS.includes(c));
  const known = new Set(userColumns);

  const reconciledVisible = settings.visibleColumns.filter(c => known.has(c));
  const reconciledOrder = settings.columnOrder.filter(c => known.has(c));

  const newColumns = userColumns.filter(c => !reconciledOrder.includes(c));
  const finalOrder = [...reconciledOrder, ...newColumns];

  if (settings.preset === 'full') {
    return {
      ...settings,
      visibleColumns: finalOrder,
      columnOrder: finalOrder,
    };
  }

  return {
    ...settings,
    visibleColumns: reconciledVisible,
    columnOrder: finalOrder,
  };
}

export default function useOutreachTableSettings(backendColumns) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const debounceRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!user?.id || initializedRef.current) return;
    initializedRef.current = true;

    let loaded = null;

    try {
      const raw = localStorage.getItem(getStorageKey(user.id));
      if (raw) {
        loaded = JSON.parse(raw);
      }
    } catch (e) {
      logger.warn('Failed to read table settings from localStorage', e);
    }

    if (!loaded && user.outreach_table_settings) {
      loaded = user.outreach_table_settings;
    }

    if (!loaded) {
      const cols = backendColumns && backendColumns.length > 0 ? backendColumns : [];
      loaded = buildPreset('compact', cols);
    }

    setSettings(loaded);
  }, [user?.id, user?.outreach_table_settings, backendColumns]);

  useEffect(() => {
    if (!settings || !backendColumns || backendColumns.length === 0) return;

    const reconciled = reconcileWithBackendColumns(settings, backendColumns);
    if (
      reconciled.visibleColumns.length !== settings.visibleColumns.length ||
      reconciled.columnOrder.length !== settings.columnOrder.length
    ) {
      setSettings(reconciled);
    }
  }, [backendColumns]);

  const persist = useCallback(
    (newSettings) => {
      if (!user?.id) return;

      try {
        localStorage.setItem(getStorageKey(user.id), JSON.stringify(newSettings));
      } catch (e) {
        logger.warn('Failed to write table settings to localStorage', e);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          await api.updateUserProfile({ outreach_table_settings: newSettings });
        } catch (e) {
          logger.warn('Failed to persist table settings to backend', e);
        }
      }, 500);
    },
    [user?.id]
  );

  const updateSettings = useCallback(
    (patch) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch, preset: 'custom' };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const applyPreset = useCallback(
    (presetId) => {
      const cols = backendColumns || [];
      const next = buildPreset(presetId, cols);
      if (settings) {
        next.columnWidths = settings.columnWidths || {};
        next.pinFirstColumn = settings.pinFirstColumn !== undefined ? settings.pinFirstColumn : true;
      }
      setSettings(next);
      persist(next);
    },
    [backendColumns, settings, persist]
  );

  const resetToDefault = useCallback(() => {
    applyPreset('compact');
  }, [applyPreset]);

  const toggleColumn = useCallback(
    (columnName) => {
      setSettings((prev) => {
        const visible = prev.visibleColumns.includes(columnName)
          ? prev.visibleColumns.filter((c) => c !== columnName)
          : [...prev.visibleColumns, columnName];
        const next = { ...prev, visibleColumns: visible, preset: 'custom' };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const reorderColumns = useCallback(
    (newOrder) => {
      setSettings((prev) => {
        const visibleSet = new Set(prev.visibleColumns);
        const reorderedVisible = newOrder.filter((c) => visibleSet.has(c));
        const next = {
          ...prev,
          columnOrder: newOrder,
          visibleColumns: reorderedVisible,
          preset: 'custom',
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const setColumnWidth = useCallback(
    (columnName, width) => {
      setSettings((prev) => {
        const next = {
          ...prev,
          columnWidths: { ...prev.columnWidths, [columnName]: width },
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const togglePinFirstColumn = useCallback(() => {
    setSettings((prev) => {
      const next = { ...prev, pinFirstColumn: !prev.pinFirstColumn };
      persist(next);
      return next;
    });
  }, [persist]);

  const displayColumns = useMemo(() => {
    if (!settings) return [];
    const orderMap = new Map(settings.columnOrder.map((c, i) => [c, i]));
    return [...settings.visibleColumns].sort((a, b) => {
      const ai = orderMap.get(a) ?? 9999;
      const bi = orderMap.get(b) ?? 9999;
      return ai - bi;
    });
  }, [settings]);

  return {
    settings,
    displayColumns,
    updateSettings,
    applyPreset,
    resetToDefault,
    toggleColumn,
    reorderColumns,
    setColumnWidth,
    togglePinFirstColumn,
    SYSTEM_COLUMNS,
  };
}
